'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { MdMenu, MdOutlinePhoneInTalk } from 'react-icons/md';
import { AiOutlineMail } from 'react-icons/ai';
import { FaRegClock } from 'react-icons/fa6';
import { LanguageSelect } from '@eliadmin/core/src/components/header/language-select';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);

  const { t } = useTranslations();

  const data = [
    {
      icon: <MdOutlinePhoneInTalk size={20} />,
      name: generalLayout?.contact?.phone ?? '',
      href: `tel:${generalLayout?.contact?.phone}`,
    },
    {
      icon: <AiOutlineMail size={20} />,
      name: generalLayout?.contact?.email ?? '',
      href: `mailto:${generalLayout?.contact?.email}`,
    },
    {
      icon: <FaRegClock size={20} />,
      name: 'Po-Pia 8:00-17:00',
    },
  ];

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Menu"
          className={cn('text-foreground', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <MdMenu size={24} />
        </SheetTrigger>
        <SheetContent
          accessibleTitle="Menu"
          side={'left'}
          className={cn(
            'flex w-full flex-col items-start justify-start p-0',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'hidden',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <div className="flex w-full items-center justify-end border-b px-4 py-5">
            <SheetClose aria-label="Zavrieť menu">
              <ButtonIcon
                alt="Zatvoriť"
                url="/images/icons/close.svg"
                className="max-h-6 min-h-6 min-w-6 max-w-6"
              />
            </SheetClose>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-6 px-4 py-6">
            <SheetClose asChild>
              <Link prefetchOnHover href={ROUTES.CATEGORIES()} className="h4 font-semibold text-foreground">
                {t('text3316')}
              </Link>
            </SheetClose>
            <SheetClose asChild>
              <Link prefetchOnHover
                href={ROUTES.SALES()}
                className="generic-hover h4 flex items-center justify-start gap-2 font-semibold text-[#EA2A30] transition-all hover:opacity-90"
              >
                <ButtonIcon
                  alt={t('text3265')}
                  url="/images/icons/sale.svg"
                  className="max-h-6 min-h-6 min-w-6 max-w-6"
                />
                {t('text3265')}
              </Link>
            </SheetClose>
            <SheetClose asChild>
              <Link href="/predajcovia" prefetch={false} className="h4 font-semibold text-foreground">
                Predajcovia
              </Link>
            </SheetClose>
            {/* <Separator /> */}
            {props.menu?.map(field => (
              <Link prefetchOnHover
                key={field.name}
                href={getSafeHref(field?.path ?? '#')}
                className="text-sm font-light text-foreground"
              >
                {field.name}
              </Link>
            ))}
            <Separator />
            <Heading title={t('text611')} tag="h2" visualTag="h5" />
            {data.map(item => {
              const Tag = item.href ? 'a' : 'div';

              return (
                <Tag
                  target={item.href ? '_blank' : undefined}
                  href={item.href}
                  key={item.name}
                  className="flex items-center justify-start gap-2 text-sm font-semibold text-foreground transition-all hover:opacity-80"
                >
                  {item.icon}
                  {item.name}
                </Tag>
              );
            })}
            {/* <LanguageSelect /> */}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
